import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, getAuthHeaders, getStoredUser } from '../auth';
import { ErrorBanner, LoadingPane, PageHeader } from '../components/page-elements';

const TruckWeighInReport = () => {
  const navigate = useNavigate();
  const user = useMemo(() => getStoredUser(), []);
  const isInternal = user?.role === 'internal' || user?.role === 'admin';

  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [error, setError] = useState('');

  const [reportYear, setReportYear] = useState(new Date().getFullYear());
  const [reportMonth, setReportMonth] = useState(new Date().getMonth() + 1);
  const [reportCustomerId, setReportCustomerId] = useState('all');
  const [reportLoading, setReportLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    if (!isInternal) return;
    setLoadingCustomers(true);
    fetch(`${API_BASE_URL}/api/customers`, {
      headers: getAuthHeaders(),
    })
      .then((response) => {
        if (!response.ok) throw new Error('Failed to load customers');
        return response.json();
      })
      .then((data) => setCustomers(Array.isArray(data) ? data : []))
      .catch((err) => setError(err.message || 'Failed to load customers'))
      .finally(() => setLoadingCustomers(false));
  }, [isInternal]);

  const buildReportQueryString = (format = 'json') => {
    const params = new URLSearchParams({
      year: String(reportYear),
      month: String(reportMonth),
      format,
    });

    if (reportCustomerId !== 'all') {
      params.set('customerId', reportCustomerId);
    }

    return params.toString();
  };

  const handleRunReport = async () => {
    setReportLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/bols/reports/truck-weigh-ins?${buildReportQueryString('json')}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.message || 'Failed to run report');
      }
      const data = await response.json();
      setReportData(data);
    } catch (err) {
      setReportData(null);
      setError(err.message || 'Failed to run report');
    } finally {
      setReportLoading(false);
    }
  };

  const handleDownloadCsv = async () => {
    setReportLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/bols/reports/truck-weigh-ins?${buildReportQueryString('csv')}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.message || 'Failed to download CSV');
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition') || '';
      const match = contentDisposition.match(/filename="?([^"]+)"?/i);
      const fileName = match?.[1] || `truck-weigh-ins-${reportYear}-${String(reportMonth).padStart(2, '0')}.csv`;

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Failed to download CSV');
    } finally {
      setReportLoading(false);
    }
  };

  if (!isInternal) {
    return <Typography>Only internal users can access truck weigh-in reports.</Typography>;
  }

  if (loadingCustomers) return <LoadingPane label="Loading report options..." />;

  return (
    <Box p={2}>
      <PageHeader
        title="Truck Weigh-In Report"
        subtitle="Monthly breakdown and detailed truck weigh-in rows."
        actions={
          <Button variant="outlined" onClick={() => navigate('/bols')}>
            Back to BOL Queue
          </Button>
        }
      />
      <ErrorBanner message={error} />

      <Card sx={{ borderRadius: 2, border: '1px solid var(--ui-border)' }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} sx={{ mb: 1.5 }}>
            <TextField
              type="number"
              label="Year"
              size="small"
              value={reportYear}
              onChange={(e) => setReportYear(e.target.value)}
              inputProps={{ min: 2020, max: 2100 }}
              sx={{ width: { xs: '100%', md: 130 } }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="truck-weigh-month-label">Month</InputLabel>
              <Select
                labelId="truck-weigh-month-label"
                value={reportMonth}
                label="Month"
                onChange={(e) => setReportMonth(e.target.value)}
              >
                {Array.from({ length: 12 }).map((_, idx) => {
                  const monthNumber = idx + 1;
                  const monthLabel = new Date(2000, idx, 1).toLocaleString(undefined, { month: 'long' });
                  return (
                    <MenuItem key={monthNumber} value={monthNumber}>
                      {monthLabel}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel id="truck-weigh-customer-label">Customer</InputLabel>
              <Select
                labelId="truck-weigh-customer-label"
                value={reportCustomerId}
                label="Customer"
                onChange={(e) => setReportCustomerId(e.target.value)}
              >
                <MenuItem value="all">All Customers</MenuItem>
                {customers.map((customer) => (
                  <MenuItem key={customer._id} value={String(customer._id)}>
                    {customer.customerName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={handleRunReport} disabled={reportLoading}>
                Run Report
              </Button>
              <Button variant="outlined" onClick={handleDownloadCsv} disabled={reportLoading}>
                Download CSV
              </Button>
            </Stack>
          </Stack>

          {reportData ? (
            <Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.25 }}>
                <Chip label={`Total Weigh-Ins: ${reportData.grandTotal?.weighInCount || 0}`} color="info" />
                <Chip label={`Customers: ${(reportData.byCustomer || []).length}`} variant="outlined" />
              </Stack>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, mb: 1.2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer</TableCell>
                      <TableCell align="right">Weigh-Ins</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(reportData.byCustomer || []).map((item) => (
                      <TableRow key={item.customerId}>
                        <TableCell>{item.customerName}</TableCell>
                        <TableCell align="right">{item.weighInCount}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="caption" color="text.secondary">
                Showing {(reportData.rows || []).length} detailed weigh-in rows for the selected period.
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Choose month/year/customer and run the report.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default TruckWeighInReport;
